import dayjs from "lib/dayjs";
import { computePortfolioDV01, computeWAL } from "lib/bondMath";
import { computeSlgsDollarDayWeightedRate } from "pages/FundPlanner/lib/structurerMetrics";

const money = (value) => {
  const n = Number(value || 0);
  return Math.abs(n) < 0.005 ? 0 : n;
};

export function buildStructurerSufficiencyRows({
  draws = [],
  investments = [],
  inflows = [],
  fundMode = "net",
} = {}) {
  if (!investments.length) return [];

  const isGrossFund = fundMode === "gross";
  const rows = [];

  inflows.forEach((flow) => {
    const amount = money(flow.inflow ?? flow.amount);
    rows.push({
      date: flow.date,
      type: "Inflow",
      description: flow.description || flow.label || "Investment inflow",
      inflow: amount,
      outflow: 0,
      netChange: amount,
    });
  });

  const withdrawalDates = isGrossFund
    ? [
        ...new Set(
          draws
            .map((draw) => draw.date || draw.drawDate || draw.maturityDate)
            .filter(Boolean)
        ),
      ]
    : draws;

  withdrawalDates.forEach((draw) => {
    const source = isGrossFund ? { date: draw } : draw;
    const amount = isGrossFund
      ? 0
      : money(source.amount ?? source.drawRequirement ?? source.outflow);

    rows.push({
      date: source.date || source.drawDate || source.maturityDate,
      type: "Outflow",
      description: "Fund Withdrawal",
      inflow: 0,
      outflow: amount,
      netChange: -amount,
    });
  });

  rows.sort((a, b) => {
    const dateDifference = new Date(a.date) - new Date(b.date);
    if (dateDifference !== 0) return dateDifference;
    return a.type === b.type ? 0 : a.type === "Inflow" ? -1 : 1;
  });

  let balance = 0;
  return rows.map((row) => {
    if (isGrossFund && row.type === "Outflow") {
      const withdrawal = money(Math.max(balance, 0));
      balance = money(balance - withdrawal);
      return {
        ...row,
        outflow: withdrawal,
        netChange: money(-withdrawal),
        balance,
      };
    }

    balance = money(balance + row.netChange);
    return { ...row, netChange: money(row.netChange), balance };
  });
}

export function buildStructurerChartData({
  draws = [],
  sufficiencyRows = [],
  fundMode = "net",
} = {}) {
  const schedule = draws
    .filter((row) => row?.drawDate && Number.isFinite(Number(row?.drawRequirement)))
    .map((row) => ({
      date: row.drawDate,
      requirement: money(row.drawRequirement),
    }));

  const isGrossFund = fundMode === "gross";
  const byDate = new Map();
  sufficiencyRows.forEach((row) => {
    const date = dayjs(row.date).format("YYYY-MM-DD");
    const item = byDate.get(date) || {
      date,
      drawAmount: 0,
      availableBalance: 0,
      balance: 0,
    };

    item.drawAmount = money(item.drawAmount + row.outflow);

    if (isGrossFund && row.type === "Outflow") {
      item.availableBalance = money(item.availableBalance + row.outflow);
    } else if (!isGrossFund && row.inflow > 0) {
      // Show the balance available before a same-day scheduled withdrawal.
      item.balance = money(row.balance);
    }

    byDate.set(date, item);
  });

  return {
    schedule,
    sufficiency: Array.from(byDate.values())
      .filter((row) => !isGrossFund || row.availableBalance > 0)
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
  };
}

export function buildStructurerStats(
  investments = [],
  settlementDate,
  sufficiencyRows = []
) {
  if (!investments.length) return null;
  const totalInvested = investments.reduce((sum, row) => sum + money(row.cost), 0);
  const totalPrincipal = investments.reduce((sum, row) => sum + money(row.principal), 0);
  const totalInterest = investments.reduce((sum, row) => sum + money(row.totalInterest), 0);
  const weightedAvgCoupon = totalPrincipal
    ? investments.reduce((sum, row) => sum + money(row.ratePct) * money(row.principal), 0) / totalPrincipal
    : 0;
  const maturities = investments.map((row) => row.maturityDate).filter(Boolean).sort();
  return {
    totalInvested,
    totalPrincipal,
    totalInterest,
    totalCashOutflows: sufficiencyRows.reduce((sum, row) => sum + money(row.outflow), 0),
    weightedAvgCoupon,
    numSecurities: investments.length,
    earliestMaturity: maturities[0] || null,
    latestMaturity: maturities[maturities.length - 1] || null,
    weightedAvgLifeYears: computeWAL(
      investments.map((row) => ({ date: row.maturityDate, principal: money(row.principal) })),
      settlementDate,
      "act/365"
    ),
    dv01Total: computePortfolioDV01(investments, 2, settlementDate),
    portfolioXirrPct: computeSlgsDollarDayWeightedRate(
      investments,
      settlementDate
    ),
  };
}

export function buildStructurerReportData(input = {}) {
  const investments = Array.isArray(input.investments) ? input.investments : [];
  const draws = Array.isArray(input.draws) ? input.draws : [];
  const hypotheticalPortfolio = input.hypotheticalPortfolio?.isHypothetical
    ? input.hypotheticalPortfolio
    : input.usTreasury?.methodology === "us-treasury"
    ? input.usTreasury
    : null;
  const sufficiencyRows = buildStructurerSufficiencyRows({
    draws,
    investments,
    inflows: Array.isArray(input.inflows) ? input.inflows : [],
    fundMode: input.fundMode,
  });
  return {
    ...input,
    draws,
    investments: [...investments].sort((a, b) => new Date(a.maturityDate) - new Date(b.maturityDate)),
    hypotheticalPortfolio,
    // Retain the legacy report field for callers loading an older Treasury result.
    usTreasury:
      hypotheticalPortfolio?.methodology === "us-treasury"
        ? hypotheticalPortfolio
        : null,
    hasResults: investments.length > 0 || Boolean(hypotheticalPortfolio?.rows?.length),
    sufficiencyRows,
    stats: hypotheticalPortfolio
      ? null
      : buildStructurerStats(
          investments,
          input.settlementDate,
          sufficiencyRows,
          input.inflows,
          input.asOfDate
        ),
    chartData: buildStructurerChartData({
      draws,
      sufficiencyRows,
      fundMode: input.fundMode,
    }),
  };
}
